import React, { useRef, useState, useEffect } from "react";
import { Typography, styled } from "@mui/material";
import Tooltip from "@hyro-components/Tooltip";
import { TOOLTIP_DELAY } from "@consts";

const HyroTruncateTypography = ({
  children,
  maxWidth,
  lines = 1,
  variant = "body2",
  component = "span",
  tooltipText,
  forceTooltip,
  sx = {},
  ...props
}) => {
  const textRef = useRef(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const showTooltip = isTextTruncated || forceTooltip;
  const tooltipTextToDisplay = tooltipText || children;

  useEffect(() => {
    const checkIfTextTruncated = () => {
      if (textRef.current) {
        let isOverflowing = false;
        if (lines === 1) {
          isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
        } else {
          isOverflowing = textRef.current.scrollHeight > textRef.current.clientHeight;
        }
        setIsTextTruncated(isOverflowing);
      }
    };

    checkIfTextTruncated();
  }, [children, maxWidth, lines]);

  const textElement = (
    <StyledTypography
      ref={textRef}
      variant={variant}
      component={component}
      maxWidth={maxWidth}
      sx={sx}
      lines={lines}
      {...props}
    >
      {children}
    </StyledTypography>
  );

  if (showTooltip) {
    return (
      <Tooltip
        title={tooltipTextToDisplay}
        enterDelay={TOOLTIP_DELAY.LONG}
        enterNextDelay={TOOLTIP_DELAY.LONG}
        tooltipStyle={{ maxWidth: "200px" }}
        popperStyle={{ marginBottom: "8px" }}
      >
        {textElement}
      </Tooltip>
    );
  }

  return textElement;
};

const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "maxWidth" && prop !== "lines"
})(({ maxWidth, lines }) => ({
  display: "inline-block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  ...(maxWidth && { maxWidth }),
  ...(lines === 1
    ? {
      whiteSpace: "nowrap"
    }
    : {
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: lines,
      whiteSpace: "normal"
    })
}));

export default HyroTruncateTypography;
